"use client";

import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type {
  ContentTypeFilter,
  ExploreBriefCard,
  ExploreStoryCard,
  FilterOption,
} from "../types";
import { SearchableTopicPills } from "./searchable-topic-pills";
import { TYPE_OPTIONS, useFilterFacets } from "./use-filter-facets";
import { useStickyAside } from "./use-sticky-aside";

// ─── Props ───────────────────────────────────────────────────

interface StoriesFilterSidebarProps {
  /** Stories already filtered by Type + Time (for computing category facets). */
  storiesForCategoryFacets: ExploreStoryCard[];
  /** Briefs for computing category facets (counts stories + briefs combined). */
  briefsForCategoryFacets?: Array<{ categorySlug: string | null }>;
  /** Stories already filtered by Type + Time + Categories (for computing tag facets). */
  storiesForTagFacets: ExploreStoryCard[];
  /** Briefs filtered by category (for computing tag/tool facets alongside stories). */
  briefsForTagFacets?: ExploreBriefCard[];
  /** All product/tool options from server (static). */
  toolOptions: FilterOption[];
  /** Tag display name lookup (slug → label). */
  tagDisplayNames: ReadonlyMap<string, string>;
  /** Category display name lookup (slug → label). */
  categoryDisplayNames: ReadonlyMap<string, string>;
  /** Category emoji lookup (slug → emoji). */
  categoryEmojiBySlug: ReadonlyMap<string, string>;

  contentType: ContentTypeFilter;
  selectedCategorySlugs: string[];
  selectedTagSlugs: string[];
  selectedToolSlugs: string[];
  hasAnyFilter: boolean;

  onContentTypeChange: (value: ContentTypeFilter) => void;
  onCategoryToggle: ({ slug }: { slug: string }) => void;
  onTagToggle: ({ slug }: { slug: string }) => void;
  onToolToggle: ({ slug }: { slug: string }) => void;
  onResetAll: () => void;
}

// ─── Component ───────────────────────────────────────────────

export function StoriesFilterSidebar({
  storiesForCategoryFacets,
  briefsForCategoryFacets,
  storiesForTagFacets,
  briefsForTagFacets,
  toolOptions,
  tagDisplayNames,
  categoryDisplayNames,
  categoryEmojiBySlug,
  contentType,
  selectedCategorySlugs,
  selectedTagSlugs,
  selectedToolSlugs,
  hasAnyFilter,
  onContentTypeChange,
  onCategoryToggle,
  onTagToggle,
  onToolToggle,
  onResetAll,
}: StoriesFilterSidebarProps) {
  const { ref, stickyTop } = useStickyAside({ mode: "fixed" });

  const selectedCategorySet = useMemo(
    () => new Set(selectedCategorySlugs),
    [selectedCategorySlugs]
  );
  const selectedTagSet = useMemo(
    () => new Set(selectedTagSlugs),
    [selectedTagSlugs]
  );
  const selectedToolSet = useMemo(
    () => new Set(selectedToolSlugs),
    [selectedToolSlugs]
  );

  const { categoryFacets, topicFacets } = useFilterFacets({
    storiesForCategoryFacets,
    briefsForCategoryFacets,
    storiesForTagFacets,
    briefsForTagFacets,
    toolOptions,
    tagDisplayNames,
    categoryDisplayNames,
    categoryEmojiBySlug,
  });
  const topicItems = useMemo(
    () =>
      topicFacets.map((topic) => ({
        ...topic,
        selected:
          topic.kind === "tool"
            ? selectedToolSet.has(topic.id)
            : selectedTagSet.has(topic.id),
        onSelect: () =>
          topic.kind === "tool"
            ? onToolToggle({ slug: topic.id })
            : onTagToggle({ slug: topic.id }),
      })),
    [onTagToggle, onToolToggle, selectedTagSet, selectedToolSet, topicFacets]
  );

  return (
    <aside
      className="hidden flex-col gap-4 self-start overflow-y-auto rounded-2xl bg-[var(--explore-bg-sidebar)] p-4 lg:sticky lg:flex"
      ref={ref}
      style={{
        top: stickyTop,
        fontFamily: "var(--homepage-sans-stack)",
        maxHeight:
          "calc(100dvh - var(--explore-sticky-top) - var(--explore-sticky-bottom-gap))",
      }}
    >
      {/* ─── Type (chip single-select) + Reset ── */}
      <div className="p-0">
        <div className="mb-3 flex items-center justify-between">
          <SidebarSectionTitle>Type</SidebarSectionTitle>
          <Button
            className={cn(
              "h-auto p-0 font-medium text-[0.72rem] no-underline",
              hasAnyFilter
                ? "text-[var(--explore-accent-teal)] hover:text-[var(--explore-accent-teal)]"
                : "pointer-events-none text-[var(--explore-text-muted)] opacity-40"
            )}
            onClick={onResetAll}
            type="button"
            variant="link"
          >
            Clear all
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {TYPE_OPTIONS.map(({ value, label }) => (
            <FilterChip
              key={value}
              label={label}
              onToggle={() =>
                onContentTypeChange(contentType === value ? "all" : value)
              }
              selected={contentType === value}
            />
          ))}
        </div>
      </div>

      {/* ─── Category (multi-select, rounded pills with emoji) ── */}
      {categoryFacets.length > 0 && (
        <FilterSection title="Category">
          <div className="flex flex-wrap gap-1.5">
            {categoryFacets.map((cat) => (
              <CategoryPill
                count={cat.count}
                emoji={cat.emoji}
                key={cat.id}
                label={cat.label}
                onToggle={() => onCategoryToggle({ slug: cat.id })}
                selected={selectedCategorySet.has(cat.id)}
              />
            ))}
          </div>
        </FilterSection>
      )}

      {/* ─── Tags + tools (multi-select chips, dynamic) ── */}
      <FilterSection title="Tags">
        <SearchableTopicPills
          emptyState="No tags or tools are visible for the current filters."
          items={topicItems}
        />
      </FilterSection>
    </aside>
  );
}

// ─── Sub-components ──────────────────────────────────────────

function SidebarSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-semibold text-[0.7rem] text-[var(--explore-text-muted)] uppercase tracking-[0.1em]">
      {children}
    </div>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border-[var(--explore-border-light)] border-t p-0 pt-4">
      <div className="mb-3">
        <SidebarSectionTitle>{title}</SidebarSectionTitle>
      </div>
      {children}
    </div>
  );
}

function FilterChip({
  label,
  selected,
  onToggle,
}: {
  label: string;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      className={cn(
        "flex items-center gap-1 rounded-sm border-0 px-2.5 py-1.5 font-medium text-[0.8rem] transition-all",
        selected
          ? "bg-[var(--explore-chip-selected-bg)] text-[var(--explore-chip-selected-text)] hover:bg-[var(--explore-chip-selected-bg)]/90"
          : "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)] hover:opacity-80"
      )}
      onClick={onToggle}
      type="button"
    >
      {label}
    </button>
  );
}

function CategoryPill({
  label,
  emoji,
  selected,
  onToggle,
  count,
}: {
  label: string;
  emoji?: string;
  selected: boolean;
  onToggle: () => void;
  count?: number;
}) {
  return (
    <button
      className={cn(
        "flex items-center gap-1.5 rounded-full border-0 px-3 py-1.5 font-medium text-[0.8rem] transition-all",
        selected
          ? "bg-[var(--explore-chip-selected-bg)] text-[var(--explore-chip-selected-text)] hover:bg-[var(--explore-chip-selected-bg)]/90"
          : "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)] hover:opacity-80"
      )}
      onClick={onToggle}
      type="button"
    >
      {emoji && <span className="text-[0.85rem]">{emoji}</span>}
      {label}
      {count !== undefined && (
        <span className="text-[0.68rem] opacity-60">{count}</span>
      )}
    </button>
  );
}
